import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError, type U2000ApiErrorResult } from '../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../U3301';
import {
  buildEventCreateBody,
  buildEventDeleteBody,
  buildEventSearchNavigateBody,
  buildEventUpdateBody,
  doCreateEvent,
  doDeleteEvent,
  doMoveEvent,
  doNavigateSearchEvents,
  doSearchEvents,
  doUpdateEvent,
  luHandleAppiError,
  type U3301EventCreatePayload,
  type U3301EventDeleteScope,
  type U3301EventMoveBody,
  type U3301EventSearchNavigatePayload,
  type U3301EventSearchNavigateResult,
  type U3301EventSearchPayload,
  type U3301EventSearchRow,
} from '../U3301Service';

export function useU3301EventSearchHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const toast = useToast();
  const { t } = useTranslation();

  const searchEvents = useCallback(
    async (payload: U3301EventSearchPayload): Promise<U3301EventSearchRow[]> => {
      try {
        const response = await doSearchEvents(axios, payload);
        return (response.data.results ?? []) as U3301EventSearchRow[];
      } catch (error) {
        const errorResult = U2000HandleApiError(error as never, t, NS, luHandleAppiError) as U2000ApiErrorResult;
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return [];
      }
    },
    [axios, t, toast],
  );

  return { searchEvents };
}

export function useU3301EventSearchNavigateHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const toast = useToast();
  const { t } = useTranslation();

  const navigateSearchEvents = useCallback(
    async (payload: U3301EventSearchNavigatePayload): Promise<U3301EventSearchNavigateResult | null> => {
      try {
        const response = await doNavigateSearchEvents(axios, buildEventSearchNavigateBody(payload));
        return response.data.result as U3301EventSearchNavigateResult;
      } catch (error) {
        const errorResult = U2000HandleApiError(error as never, t, NS, luHandleAppiError) as U2000ApiErrorResult;
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return null;
      }
    },
    [axios, t, toast],
  );

  return { navigateSearchEvents };
}

export function useU3301EventMoveHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const toast = useToast();
  const { t } = useTranslation();

  const moveEvent = useCallback(
    async (eventId: string, body: U3301EventMoveBody): Promise<U3301EventSearchRow | null> => {
      try {
        const response = await doMoveEvent(axios, eventId, body);
        return response.data.result as U3301EventSearchRow;
      } catch (error) {
        const errorResult = U2000HandleApiError(error as never, t, NS, luHandleAppiError) as U2000ApiErrorResult;
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        throw error;
      }
    },
    [axios, t, toast],
  );

  return { moveEvent };
}

export function useU3301EventMutationHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const toast = useToast();
  const { t } = useTranslation();

  const createEvent = useCallback(
    async (params: U3301EventCreatePayload): Promise<U3301EventSearchRow[] | null> => {
      try {
        const response = await doCreateEvent(axios, buildEventCreateBody(params));
        const results = (response.data.results ?? []) as U3301EventSearchRow[];
        toast.success(t('msgLogicalUnitSaved', { ns: NS }), t('successTitle', { ns: NS }));
        return results;
      } catch (error) {
        const errorResult = U2000HandleApiError(error as never, t, NS, luHandleAppiError) as U2000ApiErrorResult;
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return null;
      }
    },
    [axios, t, toast],
  );

  const updateEvent = useCallback(
    async (params: {
      id: string;
      version: number;
      title: string;
      description: string;
      eventDate: string;
      categoryId?: string;
    }): Promise<U3301EventSearchRow | null> => {
      try {
        const response = await doUpdateEvent(axios, params.id, buildEventUpdateBody(params));
        toast.success(t('msgLogicalUnitSaved', { ns: NS }), t('successTitle', { ns: NS }));
        return response.data.result as U3301EventSearchRow;
      } catch (error) {
        const errorResult = U2000HandleApiError(error as never, t, NS, luHandleAppiError) as U2000ApiErrorResult;
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return null;
      }
    },
    [axios, t, toast],
  );

  const deleteEvent = useCallback(
    async (params: { id: string; version: number; scope?: U3301EventDeleteScope }): Promise<boolean> => {
      try {
        await doDeleteEvent(axios, buildEventDeleteBody(params));
        toast.success(t('msgEventDeleted', { ns: NS }), t('successTitle', { ns: NS }));
        return true;
      } catch (error) {
        const errorResult = U2000HandleApiError(error as never, t, NS, luHandleAppiError) as U2000ApiErrorResult;
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return false;
      }
    },
    [axios, t, toast],
  );

  return { createEvent, updateEvent, deleteEvent };
}
